import logger from '@/util/logger';
import {
    atomWithLoadMore,
    AWAITING_TOPIC_APPROVAL_STATUSES,
    CATALOG_RECALL_STATUSES,
    type CatalogRecallStatus,
    RECALLS_DEFAULT_FILTER,
    RecallDao,
    RecallJobDao,
    RecallMatchDao,
    RecallVerdictDao,
    type RecallsFilter,
} from '@rock/db-admin';
import type { EquipmentId, HomeId, Recall, RecallId, RecallStatus, RecallTopicId } from '@rock/types';
import { isEquipmentRecallVerdict } from '@rock/types';
import { shallowEqual } from '@rock/util';
import { atom } from 'jotai';
import { atomFamily, atomWithRefresh } from 'jotai/utils';
import { recallTopicsState } from './recallTopics.state';

const log = logger('recalls.state');
const recallDao = new RecallDao();
const recallJobDao = new RecallJobDao();
const recallMatchDao = new RecallMatchDao();
const recallVerdictDao = new RecallVerdictDao();

export type { CatalogRecallStatus, RecallsFilter };
export { CATALOG_RECALL_STATUSES, RECALLS_DEFAULT_FILTER };

/** The recalls list, paginated, under the URL-driven filter. */
export const recallsState = atomWithLoadMore<Recall, RecallsFilter>(
    ({ limitAtom, filterAtom }) => recallDao.recallsAtom(limitAtom, filterAtom),
    { initialFilter: RECALLS_DEFAULT_FILTER },
);

/** Live candidates for `isRecallStuck`; the age cut is the caller's, at render. Normally none. */
export const stuckRecallCandidatesState = recallDao.stuckCandidatesAtom(100);

export const recallState = atomFamily((recallId: RecallId | undefined) => recallDao.recallAtom(recallId), shallowEqual);

/** `app/recalls-state`: the last sync, the versions, the untyped backlog by kind. */
export const recallsStateDocState = recallDao.recallsStateAtom();

/** The ten most recent admin jobs — the Recalls pipeline strip and a kind's Process card show the ones still running. */
export const recentRecallJobsState = recallJobDao.recentRecallJobsAtom(10);

export const recallMatchesState = atomFamily(
    (recallId: RecallId | undefined) => recallMatchDao.recallMatchesAtom(recallId),
    shallowEqual,
);

/** Every real home's verdict for one recall — the Matches tab. Seeded E2E homes are left out. */
export const recallVerdictsState = atomFamily((recallId: RecallId | undefined) => {
    const verdicts = recallVerdictDao.verdictsForRecallAtom(recallId);
    return atom(async (get) => (await get(verdicts)).filter((verdict) => !verdict.test));
}, shallowEqual);

/** One unit's verdicts — the Recalls section of the admin equipment card. */
export const equipmentRecallVerdictsState = atomFamily(
    ({ homeId, equipmentId }: { homeId: HomeId; equipmentId: EquipmentId }) => {
        const verdicts = recallVerdictDao.verdictsForEquipmentAtom(homeId, equipmentId);
        return atom(async (get) => (await get(verdicts)).filter(isEquipmentRecallVerdict));
    },
    shallowEqual,
);

/** The statuses a page shows a count for; `new` and `classified` surface as stuck or untyped instead. */
const COUNTED_STATUSES = [...CATALOG_RECALL_STATUSES, 'rejected'] as const satisfies readonly RecallStatus[];
type CountedStatus = (typeof COUNTED_STATUSES)[number];

/** A count, or undefined when that one aggregation failed; the others still show. */
export interface RecallCounts {
    all?: number;
    byStatus: Partial<Record<CountedStatus, number>>;
    /** Per catalog status, the recalls with matched units. */
    withMatches: Partial<Record<CatalogRecallStatus, number>>;
    reReview?: number;
    /** Recalls the latest reclassify proposed a type or topic for. */
    suggestions?: number;
}

/**
 * The counts the list's filters and the dashboard card show, as server aggregations over the same fields
 * the filters use — so every number is exactly how many rows its link lands on. Each count settles on its
 * own: one missing index blanks one number, not the page. Refreshable, so every visit reads current values.
 */
export const recallCountsState = atomWithRefresh(async (): Promise<RecallCounts | undefined> => {
    const settled = await Promise.allSettled([
        recallDao.countRecalls(),
        recallDao.countReReviewPending(),
        recallDao.countSuggestionsPending(),
        ...COUNTED_STATUSES.map((status) => recallDao.countRecallsWithStatus(status)),
        ...CATALOG_RECALL_STATUSES.map((status) => recallDao.countWithMatches(status)),
    ]);
    const value = (i: number) => {
        const result = settled[i];
        if (result?.status === 'fulfilled') return result.value;
        if (result?.status === 'rejected') log.error('A recall count failed', { err: result.reason, index: i });
        return undefined;
    };
    if (settled.every((result) => result.status === 'rejected')) return undefined;
    return {
        all: value(0),
        reReview: value(1),
        suggestions: value(2),
        byStatus: Object.fromEntries(COUNTED_STATUSES.map((status, i) => [status, value(3 + i)])),
        withMatches: Object.fromEntries(
            CATALOG_RECALL_STATUSES.map((status, i) => [status, value(3 + COUNTED_STATUSES.length + i)]),
        ),
    };
});

export interface RecallVerdictCounts {
    affected: number;
    possiblyAffected: number;
    /** Verdicts with `notifiedAt` set: units whose owner has been told. */
    notified: number;
}

/** Verdict counts across every real home — the dashboard's impact figures. */
export const recallVerdictCountsState = atomWithRefresh(async (): Promise<RecallVerdictCounts | undefined> => {
    try {
        const [affected, possiblyAffected, notified] = await Promise.all([
            recallVerdictDao.countVerdicts('affected'),
            recallVerdictDao.countVerdicts('possibly-affected'),
            recallVerdictDao.countNotifiedVerdicts(),
        ]);
        return { affected, possiblyAffected, notified };
    } catch (err) {
        log.error('Failed to count recall verdicts', { err });
        return undefined;
    }
});

/** Untyped recalls of one kind still `classified` — what the kind page's Process button would reach. Refreshable. */
export const unprocessedKindCountState = atomFamily((productKind: string) =>
    atomWithRefresh(async (): Promise<number | undefined> => {
        try {
            return await recallDao.countUnprocessedUntypedOfKind(productKind);
        } catch (err) {
            log.error('Failed to count a kind’s unprocessed recalls', { err, productKind });
            return undefined;
        }
    }),
);

/** How many of a topic's recalls await approval and how many are live; undefined where that count failed. */
export interface RecallTopicCounts {
    awaiting?: number;
    approved?: number;
}

/** Every topic's counts — the Topics tab's list. Refreshable, so every visit and approval reads current values. */
export const recallTopicCountsState = atomWithRefresh(
    async (get): Promise<Partial<Record<RecallTopicId, RecallTopicCounts>> | undefined> => {
        const topics = await get(recallTopicsState);
        const count = async (topicId: RecallTopicId, statuses: readonly RecallStatus[]) => {
            try {
                return await recallDao.countRecallsForTopic(topicId, statuses);
            } catch (err) {
                log.error('A recall topic count failed', { err, topicId });
                return undefined;
            }
        };
        const rows = await Promise.all(
            topics.map(async (topic) => {
                const [awaiting, approved] = await Promise.all([
                    count(topic.id, AWAITING_TOPIC_APPROVAL_STATUSES),
                    count(topic.id, ['approved']),
                ]);
                return [topic.id, { awaiting, approved }] as const;
            }),
        );
        return Object.fromEntries(rows);
    },
);

/** The most a topic's recall lists show; a topic past it is worked down by approving. */
/** Recalls awaiting approval under any topic, each once — the Topics tab's count. Refreshable like the others. */
export const recallsAwaitingTopicApprovalCountState = atomWithRefresh(async (get): Promise<number | undefined> => {
    const topics = await get(recallTopicsState);
    try {
        return await recallDao.countRecallsAwaitingTopicApproval(topics.map((topic) => topic.id));
    } catch (err) {
        log.error('The awaiting topic approval count failed', { err });
        return undefined;
    }
});

export const TOPIC_RECALLS_LIMIT = 200;

/** One topic's recalls awaiting approval, newest publish first, live — the Topics tab's queue. */
export const awaitingTopicRecallsState = atomFamily((topicId: RecallTopicId) =>
    recallDao.recallsForTopicAtom(topicId, AWAITING_TOPIC_APPROVAL_STATUSES, TOPIC_RECALLS_LIMIT),
);

/** One topic's live recalls, newest publish first. */
export const approvedTopicRecallsState = atomFamily((topicId: RecallTopicId) =>
    recallDao.recallsForTopicAtom(topicId, ['approved'], TOPIC_RECALLS_LIMIT),
);
